/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { EuiButtonIcon } from "@inscopix/ideas-eui";
import { useMouseOver } from "hooks/useMouseOver";
import { selectSystemFiles } from "utils/selectSystemFiles";
import { useDataTableContext } from "../store/DataTableProvider";
import { DragEvent, useState } from "react";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { GridApi, IRowNode } from "ag-grid-community";
import { DataTableRowData } from "../layout/DataTable";
import { useDroppable } from "@dnd-kit/core";
import { CellValue } from "@inscopix/ideas-hyperformula";
import { CellValueRendererBase } from "../cell-value-renderers/CellValueRendererBase";

const styles = {
  root: (isOver: boolean, isEmpty: boolean) => css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: ${isEmpty ? "center" : "flex-start"};
    height: 100%;
    background-color: ${isOver ? "#cce4f5" : "transparent"};
  `,
};

interface CellRendererFileProps {
  api: GridApi;
  eParentOfValue: HTMLElement;
  tableId: string;
  columnId: string;
  rowId: string;
  rowNode: IRowNode<DataTableRowData>;
  isCustomFormula: boolean;
  formula: string;
  value: CellValue;
}

/** Cell renderer that renders cells in file columns */
export const CellRendererFile = ({
  api,
  eParentOfValue,
  tableId,
  columnId,
  rowId,
  rowNode,
  isCustomFormula,
  formula,
  value,
}: CellRendererFileProps) => {
  const uploadFiles = useDataTableContext((s) => s.uploadFiles);
  const [isLoading, setIsLoading] = useState(false);
  const { isMouseOver } = useMouseOver(eParentOfValue);
  const { setNodeRef: droppableRef, isOver } = useDroppable({
    id: `${tableId}-${columnId}-${rowId}`,
    disabled: false,
    data: { address: { tableId, columnId, rowId }, value },
  });

  const handleClick = async () => {
    try {
      setIsLoading(true);
      const blobs = (await selectSystemFiles(true)) ?? [];
      if (blobs.length > 0) {
        const address = { tableId, columnId, rowId };
        await uploadFiles({ blobs, address });
        setIsLoading(false);
        addUtilityToastSuccess("File upload initiated");
      }
    } catch (error) {
      setIsLoading(false);
      addUtilityToastFailure("Failed to initiate file upload");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = async (e: DragEvent) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      const blobs = Array.from(e.dataTransfer.files);
      if (blobs.length > 0) {
        const address = { tableId, columnId, rowId };
        await uploadFiles({ blobs, address });
        setIsLoading(false);
        addUtilityToastSuccess("File upload initiated");
      }
    } catch (error) {
      setIsLoading(false);
      addUtilityToastFailure("Failed to initiate file upload");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      css={styles.root(isOver, formula === "")}
      ref={droppableRef}
      onDrop={(e) => void handleDrop(e)}
    >
      {isMouseOver && formula === "" && (
        <>
          <EuiButtonIcon
            aria-label="Upload files"
            iconType="exportAction"
            isLoading={isLoading}
            onClick={() => void handleClick()}
          />
          <EuiButtonIcon
            aria-label="Select files"
            iconType="link"
            onClick={() => {
              if (rowNode.rowIndex !== null) {
                api.startEditingCell({
                  colKey: columnId,
                  rowIndex: rowNode.rowIndex,
                });
              }
            }}
          />
        </>
      )}

      <CellValueRendererBase
        value={value}
        isDefaultValue={!isCustomFormula}
        address={{ tableId, columnId, rowId }}
        isDraggable
      />
    </div>
  );
};
